/**
 * API Endpoints Constants
 * Centralized location for all API endpoint URLs
 */

/**
 * CASA (Current Account Savings Account) endpoints
 * Base URL: environment.casaApiUrl
 */
export const CASA_ENDPOINTS = {
  CUSTOMER_ACCOUNTS: (customerId: string) => `/api/v1/casa/customers/${customerId}/accounts`,
  ACCOUNT_DETAILS: (accountId: string) => `/api/v1/casa/accounts/${accountId}`,
  ACCOUNT_BALANCE: (accountId: string) => `/api/v1/casa/accounts/${accountId}/balance`,
} as const;

/**
 * Authentication endpoints
 */
export const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REGISTER: '/auth/register',
  REFRESH_TOKEN: '/auth/refresh',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  VERIFY_EMAIL: '/auth/verify-email',
  VERIFY_OTP: '/auth/verify-otp',
  CHANGE_PASSWORD: '/auth/change-password',
} as const;

/**
 * User management endpoints
 */
export const USER_ENDPOINTS = {
  PROFILE: '/users/profile',
  UPDATE_PROFILE: '/users/profile',
  UPLOAD_AVATAR: '/users/avatar',
  PREFERENCES: '/users/preferences',
  SECURITY_SETTINGS: '/users/security',
  DELETE_ACCOUNT: '/users/delete',
} as const;

/**
 * Beneficiary query parameters interface
 *
 * Examples:
 * - Filter by active status and user: `filter: "isActive eq true and userId eq 'USER123'"`
 * - Filter by beneficiary type: `filter: "beneficiaryType eq 'DOMESTIC'"`
 * - Filter by nickname contains: `filter: "nickname contains 'John'"`
 * - Sort by created date: `sort: "createdAt desc"`
 * - Select specific fields: `fields: "beneficiaryId,nickname,beneficiaryType"`
 * - Pagination: `page: 1, pageSize: 20`
 */
export interface BeneficiaryQueryParams {
  filter?: string; // OData-style filter: e.g., "isActive eq true and userId eq 'USER123'"
  sort?: string; // Sort field and direction: e.g., "createdAt desc"
  fields?: string; // Comma-separated fields: e.g., "beneficiaryId,nickname,beneficiaryType"
  page?: number; // Page number (1-based)
  pageSize?: number; // Number of items per page
}

/**
 * Beneficiary management endpoints
 */
export const BENEFICIARY_ENDPOINTS = {
  // Core Beneficiary APIs
  /**
   * List beneficiaries with optional query parameters
   *
   * @param params - Query parameters for filtering, sorting, pagination, and field selection
   *
   * @example
   * // Basic usage with filter
   * LIST_BY_CUSTOMER({ filter: "userId eq 'USER123'" })
   * // Result: /beneficiaries/query?filter=userId%20eq%20'USER123'
   *
   * @example
   * // Filter by beneficiary type
   * LIST_BY_CUSTOMER({ filter: "beneficiaryType eq 'DOMESTIC'" })
   * // Result: /beneficiaries/query?filter=beneficiaryType%20eq%20'DOMESTIC'
   *
   * @example
   * // Filter with sorting
   * LIST_BY_CUSTOMER({
   *   filter: "beneficiaryType eq 'DOMESTIC'",
   *   sort: "createdAt desc"
   * })
   * // Result: /beneficiaries/query?filter=beneficiaryType%20eq%20'DOMESTIC'&sort=createdAt%20desc
   *
   * @example
   * // Filter with field selection
   * LIST_BY_CUSTOMER({
   *   filter: "nickname contains 'John'",
   *   fields: "beneficiaryId,nickname,beneficiaryType"
   * })
   * // Result: /beneficiaries/query?filter=nickname%20contains%20'John'&fields=beneficiaryId,nickname,beneficiaryType
   *
   * @example
   * // Pagination only
   * LIST_BY_CUSTOMER({ page: 1, pageSize: 20 })
   * // Result: /beneficiaries/query?page=1&pageSize=20
   *
   * @example
   * // Combined - Filter, Sort, Fields, and Pagination
   * LIST_BY_CUSTOMER({
   *   filter: "isActive eq true and userId eq 'USER123'",
   *   sort: "createdAt desc",
   *   fields: "beneficiaryId,nickname,beneficiaryType",
   *   page: 1,
   *   pageSize: 20
   * })
   * // Result: /beneficiaries/query?filter=isActive%20eq%20true%20and%20userId%20eq%20'USER123'&sort=createdAt%20desc&fields=beneficiaryId,nickname,beneficiaryType&page=1&pageSize=20
   */
  LIST_BY_CUSTOMER: (params?: BeneficiaryQueryParams) => {
    let endpoint = `/beneficiaries/query`;
    const queryParams: string[] = [];

    if (params) {
      if (params.filter) {
        queryParams.push(`filter=${encodeURIComponent(params.filter)}`);
      }
      if (params.sort) {
        queryParams.push(`sort=${encodeURIComponent(params.sort)}`);
      }
      if (params.fields) {
        queryParams.push(`fields=${encodeURIComponent(params.fields)}`);
      }
      if (params.page !== undefined) {
        queryParams.push(`page=${params.page}`);
      }
      if (params.pageSize !== undefined) {
        queryParams.push(`pageSize=${params.pageSize}`);
      }
    }

    if (queryParams.length > 0) {
      endpoint += `?${queryParams.join('&')}`;
    }

    return endpoint;
  },
  CREATE: '/beneficiaries',
  UPDATE: '/beneficiaries', // PUT /beneficiaries with beneficiaryId in body
  DELETE: (id: string | number) => `/beneficiaries/${id}`,
  GET_BY_ID: (id: string | number) => `/beneficiaries/${id}`,
  ACTIVATE: (id: string | number) => `/beneficiaries/${id}/activate`,
  DEACTIVATE: (id: string | number) => `/beneficiaries/${id}/deactivate`,

  // Payment Integration APIs
  DOMESTIC_PAYMENT_BENEFICIARIES: (cifId: string | number) =>
    `/payment/customers/${cifId}/beneficiaries`,
  INTERNATIONAL_PAYMENT_BENEFICIARIES: (cifId: string | number) =>
    `/payment/international/beneficiaries/${cifId}`,

  // Beneficiary Name Verification API
  VERIFY_NAME: '/payment/beneficiaries/verify-name',
} as const;

/**
 * Payment endpoints
 */
export const PAYMENT_ENDPOINTS = {
  LIST: '/payments',
  CREATE: '/payments',
  GET_BY_ID: (id: string | number) => `/payments/${id}`,
  CANCEL: (id: string | number) => `/payments/${id}/cancel`,
  HISTORY: '/payments/history',
  RECEIPT: (id: string | number) => `/payments/${id}/receipt`,
  STATUS: (id: string | number) => `/payments/${id}/status`,
  FEES: '/payments/fees',
} as const;

/**
 * Trading endpoints
 */
export const TRADING_ENDPOINTS = {
  ORDERS: '/trading/orders',
  CREATE_ORDER: '/trading/orders',
  CANCEL_ORDER: (id: string | number) => `/trading/orders/${id}/cancel`,
  ORDER_HISTORY: '/trading/orders/history',
  POSITIONS: '/trading/positions',
  MARKET_DATA: '/trading/market-data',
  QUOTES: '/trading/quotes',
  INSTRUMENTS: '/trading/instruments',
} as const;

/**
 * Dashboard and analytics endpoints
 */
export const DASHBOARD_ENDPOINTS = {
  OVERVIEW: '/dashboard/overview',
  ANALYTICS: '/dashboard/analytics',
  REPORTS: '/dashboard/reports',
  NOTIFICATIONS: '/dashboard/notifications',
  RECENT_ACTIVITY: '/dashboard/activity',
  STATISTICS: '/dashboard/statistics',
} as const;

/**
 * File upload endpoints
 */
export const FILE_ENDPOINTS = {
  UPLOAD: '/files/upload',
  DOWNLOAD: (id: string | number) => `/files/${id}/download`,
  DELETE: (id: string | number) => `/files/${id}`,
  LIST: '/files',
} as const;

/**
 * Notification endpoints
 */
export const NOTIFICATION_ENDPOINTS = {
  LIST: '/notifications',
  MARK_READ: (id: string | number) => `/notifications/${id}/read`,
  MARK_ALL_READ: '/notifications/mark-all-read',
  DELETE: (id: string | number) => `/notifications/${id}`,
  PREFERENCES: '/notifications/preferences',
} as const;

/**
 * Settings endpoints
 */
export const SETTINGS_ENDPOINTS = {
  GENERAL: '/settings/general',
  SECURITY: '/settings/security',
  NOTIFICATIONS: '/settings/notifications',
  LANGUAGE: '/settings/language',
  THEME: '/settings/theme',
  EXPORT_DATA: '/settings/export',
  IMPORT_DATA: '/settings/import',
} as const;

/**
 * System endpoints
 */
export const SYSTEM_ENDPOINTS = {
  HEALTH: '/system/health',
  VERSION: '/system/version',
  CONFIG: '/system/config',
  MAINTENANCE: '/system/maintenance',
} as const;

/**
 * Loan query parameters interface
 *
 * Examples:
 * - Filter by status and onboarding UUID: `filter: "status eq 'APPROVED' and onboardingUuid eq 'UUID123'"`
 * - Filter by product type: `filter: "productType eq 'PERSONAL_LOAN'"`
 * - Filter by requested amount: `filter: "requestedAmount gt 100000"`
 * - Sort by created date: `sort: "createdAt desc"`
 * - Select specific fields: `fields: "uuid,requestedAmount,status,createdAt"`
 * - Pagination: `page: 1, pageSize: 20`
 */
export interface LoanQueryParams {
  filter?: string; // OData-style filter: e.g., "status eq 'APPROVED' and onboardingUuid eq 'UUID123'"
  sort?: string; // Sort field and direction: e.g., "createdAt desc"
  fields?: string; // Comma-separated fields: e.g., "uuid,requestedAmount,status,createdAt"
  page?: number; // Page number (1-based)
  pageSize?: number; // Number of items per page
}

/**
 * Lending/Loan endpoints
 * Based on LOAN_AND_CREDIT_MODULE_DOCUMENTATION.md
 */
export const LOAN_ENDPOINTS = {
  // Loan listing and details
  /**
   * Query loan applications with optional filters, sorting, and pagination
   *
   * @param params - Query parameters for filtering, sorting, pagination, and field selection
   *
   * @example
   * // Filter by status and onboarding UUID
   * QUERY({ filter: "status eq 'APPROVED' and onboardingUuid eq '1000000011'" })
   * // Result: /lending/applications/query?filter=status%20eq%20'APPROVED'%20and%20onboardingUuid%20eq%20'1000000011'
   *
   * @example
   * // Filter by product type with sorting
   * QUERY({
   *   filter: "productType eq 'PERSONAL_LOAN'",
   *   sort: "createdAt desc"
   * })
   * // Result: /lending/applications/query?filter=productType%20eq%20'PERSONAL_LOAN'&sort=createdAt%20desc
   *
   * @example
   * // Filter with pagination
   * QUERY({
   *   filter: "requestedAmount gt 100000",
   *   fields: "uuid,requestedAmount,status,createdAt",
   *   page: 1,
   *   pageSize: 20
   * })
   * // Result: /lending/applications/query?filter=requestedAmount%20gt%20100000&fields=uuid,requestedAmount,status,createdAt&page=1&pageSize=20
   */
  QUERY: (params?: LoanQueryParams) => {
    let endpoint = `/lending/applications/query`;
    const queryParams: string[] = [];

    if (params) {
      if (params.filter) {
        queryParams.push(`filter=${encodeURIComponent(params.filter)}`);
      }
      if (params.sort) {
        queryParams.push(`sort=${encodeURIComponent(params.sort)}`);
      }
      if (params.fields) {
        queryParams.push(`fields=${encodeURIComponent(params.fields)}`);
      }
      if (params.page !== undefined) {
        queryParams.push(`page=${params.page}`);
      }
      if (params.pageSize !== undefined) {
        queryParams.push(`pageSize=${params.pageSize}`);
      }
    }

    if (queryParams.length > 0) {
      endpoint += `?${queryParams.join('&')}`;
    }

    return endpoint;
  },
  LIST: (onboardingUuid: string, type?: string) => {
    const filters: string[] = [`onboardingUuid eq '${onboardingUuid}'`];
    if (type) {
      filters.push(`productType eq '${type}'`);
    }
    return LOAN_ENDPOINTS.QUERY({
      filter: filters.join(' and '),
      sort: 'createdAt desc',
    });
  },
  DETAILS: (loanUuid: string) => `/lending/applications/loans/${encodeURIComponent(loanUuid)}`,

  // Application submission
  SUBMIT_APPLICATION: '/lending/applications/submit',
  CREATE_APPLICATION: '/lending/applications',

  // Products
  PRODUCTS: '/lending/products',
  PRODUCT_BY_TYPE: (productType: string) => `/lending/products/${encodeURIComponent(productType)}`,

  // OTP Verification (Credit OTP as per React flow)
  VERIFY_CREDIT_OTP: (applicationId: string, otp: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/verify-credit-otp?otp=${encodeURIComponent(otp)}`,
  VERIFY_OTP: (applicationId: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/verify-otp`,
  RESEND_OTP: (applicationId: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/resend-otp`,

  // Document management
  UPLOAD_DOCUMENT: (applicationId: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/documents`,
  UPLOAD_DOCUMENTS: (applicationId: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/upload-documents`,
  UPLOAD_CONSENT: '/lending/documents/upload',
  GET_DOCUMENTS: (applicationId: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/documents`,
  DELETE_DOCUMENT: (applicationId: string, documentId: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/documents/${encodeURIComponent(documentId)}`,

  // Credit decision
  CREDIT_DECISION: (applicationId: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/credit-decision`,

  // E-Signature / Sign
  E_SIGNATURE: (applicationId: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/esignature`,
  SIGN: (applicationId: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/sign`,
  SIGNATURE_STATUS: (signatureId: string) =>
    `/lending/signatures/${encodeURIComponent(signatureId)}/status`,

  // Disbursement
  DISBURSE: (applicationId: string) =>
    `/lending/applications/${encodeURIComponent(applicationId)}/disburse`,

  // Pre-approved offers
  PRE_APPROVED_OFFERS: (customerId: string) =>
    `/lending/pre-approved/${encodeURIComponent(customerId)}`,
  ACCEPT_OFFER: (offerId: string) => `/lending/pre-approved/${encodeURIComponent(offerId)}/accept`,
  GET_AGREEMENT: (agreementId: string) => `/lending/agreements/${encodeURIComponent(agreementId)}`,
  SIGN_AGREEMENT: (agreementId: string) =>
    `/lending/agreements/${encodeURIComponent(agreementId)}/sign`,
} as const;

/**
 * Account query parameters interface
 *
 * Examples:
 * - Filter by customer ID: `filter: "customerId eq '0013QAKRAMXB'"`
 * - Filter by account status: `filter: "accountStatus eq 'ACTIVE'"`
 * - Filter by account type: `filter: "accountType eq 'CASA'"`
 * - Sort by created date: `sort: "createdAt desc"`
 * - Pagination: `page: 1, pageSize: 20`
 */
export interface AccountQueryParams {
  filter?: string; // OData-style filter: e.g., "customerId eq '0013QAKRAMXB'"
  sort?: string; // Sort field and direction: e.g., "createdAt desc"
  fields?: string; // Comma-separated fields
  page?: number; // Page number (1-based)
  pageSize?: number; // Number of items per page
}

/**
 * Account endpoints
 * Base URL: /api/v1/accounts
 */
export const ACCOUNT_ENDPOINTS = {
  /**
   * Query accounts with optional filters, sorting, and pagination
   *
   * @param params - Query parameters for filtering, sorting, pagination
   *
   * @example
   * // Filter by customer ID
   * QUERY({ filter: "customerId eq '0013QAKRAMXB'" })
   * // Result: /api/v1/accounts/query?filter=customerId%20eq%20'0013QAKRAMXB'
   *
   * @example
   * // Filter with sorting and pagination
   * QUERY({
   *   filter: "customerId eq '0013QAKRAMXB'",
   *   sort: "createdAt desc",
   *   page: 1,
   *   pageSize: 10
   * })
   */
  QUERY: (params?: AccountQueryParams) => {
    let endpoint = `/api/v1/accounts/query`;
    const queryParams: string[] = [];

    if (params) {
      if (params.filter) {
        queryParams.push(`filter=${encodeURIComponent(params.filter)}`);
      }
      if (params.sort) {
        queryParams.push(`sort=${encodeURIComponent(params.sort)}`);
      }
      if (params.fields) {
        queryParams.push(`fields=${encodeURIComponent(params.fields)}`);
      }
      if (params.page !== undefined) {
        queryParams.push(`page=${params.page}`);
      }
      if (params.pageSize !== undefined) {
        queryParams.push(`pageSize=${params.pageSize}`);
      }
    }

    if (queryParams.length > 0) {
      endpoint += `?${queryParams.join('&')}`;
    }

    return endpoint;
  },

  /**
   * Get account details by account ID
   */
  GET_BY_ID: (accountId: string) => `/api/v1/accounts/${encodeURIComponent(accountId)}`,

  /**
   * Get account balance by account ID
   */
  GET_BALANCE: (accountId: string) => `/api/v1/accounts/${encodeURIComponent(accountId)}/balance`,
} as const;

/**
 * All API endpoints grouped together
 */
export const API_ENDPOINTS = {
  CASA: CASA_ENDPOINTS,
  AUTH: AUTH_ENDPOINTS,
  USER: USER_ENDPOINTS,
  BENEFICIARY: BENEFICIARY_ENDPOINTS,
  PAYMENT: PAYMENT_ENDPOINTS,
  TRADING: TRADING_ENDPOINTS,
  DASHBOARD: DASHBOARD_ENDPOINTS,
  FILE: FILE_ENDPOINTS,
  NOTIFICATION: NOTIFICATION_ENDPOINTS,
  SETTINGS: SETTINGS_ENDPOINTS,
  SYSTEM: SYSTEM_ENDPOINTS,
  LOAN: LOAN_ENDPOINTS,
  ACCOUNT: ACCOUNT_ENDPOINTS,
} as const;
